import { Injectable } from '@angular/core';
import { PartnerCampaignCollateral } from '@core/models/partner-campaign.model';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { PartnerCampaignResources } from './partner-campaign.resources';
import { PartnerCampaignState } from './partner-campaign.state';
import { firstValueFrom } from 'rxjs';


@AttachYCState(PartnerCampaignState)
@Injectable({ providedIn: 'root' })
export class PartnerCampaignService extends BaseYCService<PartnerCampaignState> {

  constructor (
    private partnerCampaignResources: PartnerCampaignResources
  ) {
    // Clean up corrupted cache BEFORE calling super() to avoid JSON.parse errors
    // This handles the migration from the old bug where default was [] instead of null
    try {
      Object.keys(localStorage)
        .filter(key => key.includes('yc-cache-') && key.includes('currentPartnerCampaignCollateral'))
        .forEach(key => {
          const value = localStorage.getItem(key);
          if (value === 'undefined' || value === '[]') {
            localStorage.removeItem(key);
          }
        });
    } catch (e) { /* Silently fail */ }

    super();
  }

  get partnerCampaignCollateralIsOpen () {
    return this.get('partnerCampaignCollateralIsOpen');
  }

  setPartnerCampaignCollateralIsOpen (isOpen: boolean) {
    this.set('partnerCampaignCollateralIsOpen', isOpen);
  }

  get currentPartnerCampaignCollateral () {
    return this.get('currentPartnerCampaignCollateral');
  }

  setCurrentPartnerCampaignCollateral (currentPartnerCampaignCollateral: PartnerCampaignCollateral) {
    // Normalize undefined or empty array to null for proper cache handling
    currentPartnerCampaignCollateral =
      currentPartnerCampaignCollateral === undefined
        || Array.isArray(currentPartnerCampaignCollateral) ?
          null :
          currentPartnerCampaignCollateral;

    this.set('currentPartnerCampaignCollateral', currentPartnerCampaignCollateral);
  }

  get hasCurrentPartnerCampaign (): boolean {
    return !!this.currentPartnerCampaignCollateral;
  }

  clearDataOnNonprofitChange () {
    this.setCurrentPartnerCampaignCollateral(null);
  }

  buildPartnerLink (
    nonprofitRegistrationId: string,
    nonprofitRegistrationAuthorityNickname: string
  ) {
    let urlString = this.currentPartnerCampaignCollateral.linkToPartnerSite;

    if (urlString && /^https?:\/\//i.test(urlString)) {
      urlString += (urlString.match(/[\?]/g) ? '&' : '?') + 'npoprefregid=' + nonprofitRegistrationId + '&npopreftype=' + encodeURIComponent(nonprofitRegistrationAuthorityNickname);
    }

    return urlString;
  }

  async getPartnerCampaignCollateral () {
    const response = await firstValueFrom(this.partnerCampaignResources.getPartnerCampaignCollateral()) as PartnerCampaignCollateral;

    this.setCurrentPartnerCampaignCollateral(response);

    return response;
  }

  async updateCollateralImpression (
    partnerCampaignCollateralId: number
  ) {
    await firstValueFrom(this.partnerCampaignResources.updateCollateralImpression(partnerCampaignCollateralId));
  }
}
